import React from "react";
import { Link } from "react-router-dom";
import {
  Globe,
  GraduationCap,
  Handshake,
  Link2,
  RefreshCw,
  Rocket,
  School,
  Target,
  Zap,
} from "lucide-react";
import Reveal from "../components/ui/Reveal";
import IconBox from "../components/ui/IconBox";
import TeamMemberCard from "../components/ui/TeamMemberCard";
import SEO, { generateBreadcrumbSchema, generateOrganizationSchema } from "../components/seo/SEO";
import { teamMembers } from "../data/team";

const values = [
  {
    icon: Target,
    title: "Student-First",
    description: "Every decision we make starts with one question: does this help students succeed? Our users aren't just customers – they're the reason we exist.",
  },
  {
    icon: Link2,
    title: "Continuity",
    description: "We believe in building lasting relationships. The bridge strategy – supporting students from pre-university through graduation – is core to who we are.",
  },
  {
    icon: Rocket,
    title: "Innovation",
    description: "We leverage AI and modern technology not for buzzwords, but to solve real problems students face every day in their academic journey.",
  },
  {
    icon: Handshake,
    title: "Accessibility",
    description: "Quality education tools should be available to every Nigerian student, regardless of location or economic background. We're building for scale.",
  },
  {
    icon: Zap,
    title: "Empowerment",
    description: "We don't just help students pass exams – we help them develop habits, skills, and confidence that serve them throughout life.",
  },
  {
    icon: RefreshCw,
    title: "Iteration",
    description: "We listen, learn, and improve constantly. Student feedback drives our roadmap. We ship fast and iterate based on real usage.",
  },
];

const timeline = [
  {
    year: "2023",
    title: "The Beginning",
    description: "CampusGenie started as a JAMB prep tool, helping students practice with past questions and CBT simulation.",
  },
  {
    year: "2024",
    title: "AI Integration",
    description: "We added AI-powered features including study planner, question generation, and personalized recommendations.",
  },
  {
    year: "2024",
    title: "Gamification",
    description: "Challenges, streaks, leaderboards, and achievements transformed studying into an engaging daily habit.",
  },
  {
    year: "2025",
    title: "The Bridge",
    description: "We launched university features, enabling the same app to support students from pre-uni through graduation.",
  },
  {
    year: "2025+",
    title: "The Future",
    description: "Group chats, AI assistants, internships, hostels, and campus life features to make CampusGenie the complete student companion.",
  },
];

const stats = [
  { value: "1K+", label: "Active Students" },
  { value: "2023", label: "Founded" },
  { value: "15+", label: "Core Features" },
  { value: "4-7 yrs", label: "Retention Goal" },
];

export default function AboutPage() {
  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "About", url: "/about" },
  ];

  const structuredData = [
    generateOrganizationSchema(),
    generateBreadcrumbSchema(breadcrumbs),
  ];

  return (
    <>
      <SEO
        title="About Us - Our Mission & Story"
        description="CampusGenie is on a mission to be the single app that stays with every Nigerian student from Admisson preparation to university graduation. Learn about our story, values, and vision for the future of student success."
        url="/about"
        structuredData={structuredData}
      />

      {/* Hero */}
      <section className="about-hero">
        <div className="container">
          <Reveal>
            <span className="eyebrow">About CampusGenie</span>
            <h1 className="heading-hero">
              Building the future of Nigerian student success.
            </h1>
            <p className="body-large">
              We're on a mission to be the single app that stays with every Nigerian student from wanting to get in to graduating – and beyond.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Stats */}
      <section className="section" style={{ paddingTop: 0 }}>
        <div className="container">
          <Reveal>
            <div className="stats-grid">
              {stats.map((stat) => (
                <div key={stat.label} className="stat-card">
                  <div className="stat-value">{stat.value}</div>
                  <div className="stat-label">{stat.label}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Mission Section */}
      <section className="section section-alt">
        <div className="container">
          <Reveal>
            <div className="mission-section">
              <span className="eyebrow" style={{ color: "#8ef4ad" }}>Our Mission</span>
              <h2 className="heading-2">
                One app. One identity. One journey that spans years of academic life.
              </h2>
              <p className="body-large">
                Most exam apps die after the exam. We saw an opportunity: the same student who needs JAMB prep today will need university tools tomorrow. Why make them start over? CampusGenie bridges these stages, creating a continuous relationship that supports students throughout their entire academic journey.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* The Problem We Solve */}
      <section className="section">
        <div className="container">
          <div className="showcase-grid">
            <Reveal>
              <div className="showcase-content">
                <span className="eyebrow">The Problem</span>
                <h2 className="heading-2">
                  students deserve better tools.
                </h2>
                <p className="body-large">
                  Today's students juggle multiple apps for exam prep, scattered WhatsApp groups for class communication, manual timetables, and no centralized place for opportunities like internships.
                </p>
                <div className="showcase-list">
                  <div className="showcase-list-item">Exam apps that disappear after JAMB</div>
                  <div className="showcase-list-item">Fragmented class communication across WhatsApp</div>
                  <div className="showcase-list-item">No unified academic planning tools</div>
                  <div className="showcase-list-item">Limited access to opportunities and support</div>
                </div>
              </div>
            </Reveal>
            
            <Reveal delay={0.15}>
              <div className="showcase-content">
                <span className="eyebrow">Our Solution</span>
                <h2 className="heading-2">
                  Everything students need, in one place.
                </h2>
                <p className="body-large">
                  CampusGenie combines exam prep, AI-powered learning, academic planning, campus communication, and life services into a single, continuous experience.
                </p>
                <div className="showcase-list">
                  <div className="showcase-list-item">Pre-uni prep that transitions to university tools</div>
                  <div className="showcase-list-item">Course-based groups with timetable sync</div>
                  <div className="showcase-list-item">AI assistants for every academic need</div>
                  <div className="showcase-list-item">Internships, hostels, and campus life support</div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section section-alt">
        <div className="container">
          <Reveal>
            <div className="section-heading section-heading-center">
              <span className="eyebrow">Our Values</span>
              <h2 className="heading-2">
                What drives us every day.
              </h2>
            </div>
          </Reveal>

          <div className="values-grid">
            {values.map((value, index) => (
              <Reveal key={value.title} delay={index * 0.1}>
                <div className="value-card">
                  <div className="value-icon">
                    <IconBox icon={value.icon} size={28} />
                  </div>
                  <h3 className="heading-4">{value.title}</h3>
                  <p className="body-small">{value.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="section">
        <div className="container">
          <Reveal>
            <div className="section-heading section-heading-center">
              <span className="eyebrow">Our Journey</span>
              <h2 className="heading-2">
                From JAMB prep to complete student companion.
              </h2>
            </div>
          </Reveal>

          <div style={{ maxWidth: "800px", margin: "0 auto" }}>
            {timeline.map((item, index) => (
              <Reveal key={item.year + item.title} delay={index * 0.1}>
                <div style={{
                  display: "flex",
                  gap: "24px",
                  padding: "24px 0",
                  borderBottom: index < timeline.length - 1 ? "1px solid var(--color-border)" : "none",
                }}>
                  <div style={{
                    flexShrink: 0,
                    width: "80px",
                    height: "80px",
                    background: "var(--gradient-primary)",
                    borderRadius: "16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontWeight: 700,
                    fontSize: "18px",
                  }}>
                    {item.year}
                  </div>
                  <div>
                    <h4 className="heading-4" style={{ marginBottom: "8px" }}>{item.title}</h4>
                    <p className="body-small">{item.description}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="section section-alt" id="team">
        <div className="container">
          <Reveal>
            <div className="section-heading section-heading-center">
              <span className="eyebrow">Our Team</span>
              <h2 className="heading-2">
                The people building CampusGenie.
              </h2>
              <p className="body-large" style={{ maxWidth: "640px", margin: "0 auto" }}>
                A small, focused team obsessed with student success shipping fast, listening to users, and building for the long journey from admission to graduation.
              </p>
            </div>
          </Reveal>

          <div className="team-grid">
            {teamMembers.map((member, index) => (
              <Reveal key={member.name} delay={index * 0.08}>
                <TeamMemberCard member={member} />
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.2}>
            <p className="team-hiring-note body-small">
              Want to join us?{" "}
              <Link to="/careers" className="legal-prose-link">
                See open roles →
              </Link>
            </p>
          </Reveal>
        </div>
      </section>

      {/* The Vision */}
      <section className="section section-dark">
        <div className="container">
          <Reveal>
            <div className="section-heading section-heading-center">
              <span className="eyebrow">The Vision</span>
              <h2 className="heading-2" style={{ color: "white", maxWidth: "16ch", margin: "0 auto 24px" }}>
                The default app for every Nigerian student.
              </h2>
              <p className="body-large" style={{ maxWidth: "700px", margin: "0 auto" }}>
                We envision a future where every Nigerian student, from secondary school through university and into their career, has access to the tools and support they need to succeed. CampusGenie will be that constant companion – always relevant, always helpful, always there.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="grid grid-3" style={{ marginTop: "48px" }}>
              <div className="feature-card" style={{ 
                background: "rgba(255,255,255,0.05)", 
                borderColor: "rgba(255,255,255,0.1)",
                textAlign: "center"
              }}>
                <div style={{ marginBottom: "16px", display: "flex", justifyContent: "center", color: "rgba(255,255,255,0.9)" }}>
                  <GraduationCap size={40} strokeWidth={1.75} aria-hidden />
                </div>
                <h4 className="heading-4" style={{ color: "white" }}>Millions of Students</h4>
                <p className="body-small" style={{ color: "rgba(255,255,255,0.7)" }}>
                  Serving students across Nigeria and eventually all of Africa.
                </p>
              </div>
              <div className="feature-card" style={{ 
                background: "rgba(255,255,255,0.05)", 
                borderColor: "rgba(255,255,255,0.1)",
                textAlign: "center"
              }}>
                <div style={{ marginBottom: "16px", display: "flex", justifyContent: "center", color: "rgba(255,255,255,0.9)" }}>
                  <School size={40} strokeWidth={1.75} aria-hidden />
                </div>
                <h4 className="heading-4" style={{ color: "white" }}>Institutional Partnerships</h4>
                <p className="body-small" style={{ color: "rgba(255,255,255,0.7)" }}>
                  Official app for universities and secondary schools.
                </p>
              </div>
              <div className="feature-card" style={{ 
                background: "rgba(255,255,255,0.05)", 
                borderColor: "rgba(255,255,255,0.1)",
                textAlign: "center"
              }}>
                <div style={{ marginBottom: "16px", display: "flex", justifyContent: "center", color: "rgba(255,255,255,0.9)" }}>
                  <Globe size={40} strokeWidth={1.75} aria-hidden />
                </div>
                <h4 className="heading-4" style={{ color: "white" }}>Alumni Network</h4>
                <p className="body-small" style={{ color: "rgba(255,255,255,0.7)" }}>
                  Lifetime relationship through mentorship and opportunities.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Join Us */}
      <section className="section" id="careers">
        <div className="container">
          <Reveal>
            <div className="section-heading section-heading-center">
              <span className="eyebrow">Join the Team</span>
              <h2 className="heading-2">
                Help us build the future of education.
              </h2>
              <p className="body-large" style={{ maxWidth: "600px", margin: "0 auto 32px" }}>
                We're always looking for passionate people who want to make a real impact on student success in Nigeria and beyond.
              </p>
              <Link to="/contact" className="btn btn-primary btn-lg">
                Get in Touch
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="section section-alt">
        <div className="container">
          <Reveal>
            <div className="cta-section">
              <div className="cta-content">
                <h2 className="heading-2">
                  Ready to be part of the story?
                </h2>
                <p className="body-large">
                  Whether you're a student, institution, or potential partner, we'd love to hear from you.
                </p>
                <div className="cta-buttons">
                  <Link to="/contact" className="btn btn-primary btn-lg">
                    Contact Us
                  </Link>
                  <Link to="/students" className="btn btn-secondary btn-lg">
                    For Students
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
